import { useQuery } from "@tanstack/react-query"
import {
  Breadcrumb,
  Button,
  Card,
  Divider,
  Form,
  Input,
  Modal,
  Tooltip,
  Typography,
  message,
  Upload,
  Select,
} from "antd"
import type React from "react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import SystemSetTable from "./components/SystemSetTable"
import { SystemSetReorderModal } from "./components/SystemSetReorderModal"
import { SystemSetSettingsModal } from "./components/SystemSetSettingsModal"
import { setService, type SetSearchParams, type SetApiParams } from "@/services/dictionary/setApi"
import { vocabularyService } from "@/services/dictionary/vocabularyApi"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import * as XLSX from "xlsx"
import { useTableParams } from "@/hooks/useTableParams"
import {
  HomeOutlined,
  FolderAddOutlined,
  FolderOutlined,
  ReloadOutlined,
  FileAddOutlined,
  UploadOutlined,
  DeleteOutlined,
  OrderedListOutlined,
  SettingOutlined,
} from "@ant-design/icons"

const { Text } = Typography

export const SET_TAG_LIST = [
  "Band",
  "Kỹ năng",
  "THPT",
  "THCS",
  "Tiểu học",
  "Chủ đề",
  "Sách",
  "Thi cử",
  "Lộ trình",
  "Bài học",
  "Đề thi",
] as const

export const SET_TAG_OPTIONS = SET_TAG_LIST.map((tag) => ({
  label: tag,
  value: tag,
}))

export const SystemSetPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useTableParams<SetSearchParams>({
    ...PAGE_CONFIG,
    search: "",
    visibility: undefined,
    parent_id: "null",
  })
  const [total, setTotal] = useState<number>(0)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  // Folder Modal state
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false)
  const [folderForm] = Form.useForm()
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [createFolderImageFile, setCreateFolderImageFile] = useState<File | null>(null)
  const [createFolderImagePreview, setCreateFolderImagePreview] = useState<string | null>(null)

  // Set Modal state
  const [isSetModalOpen, setIsSetModalOpen] = useState(false)
  const [setForm] = Form.useForm()
  const [creatingSet, setCreatingSet] = useState(false)
  const [createSetImageFile, setCreateSetImageFile] = useState<File | null>(null)
  const [createSetImagePreview, setCreateSetImagePreview] = useState<string | null>(null)

  // Edit Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editForm] = Form.useForm()
  const [editingSet, setEditingSet] = useState<any>(null)
  const [updatingSet, setUpdatingSet] = useState(false)
  const [editSetImagePreview, setEditSetImagePreview] = useState<string | null>(null)
  const [editSetImageFile, setEditSetImageFile] = useState<File | null>(null)
  const [editSetImageDeleted, setEditSetImageDeleted] = useState(false)
  const [isUploadingExcel, setIsUploadingExcel] = useState(false)

  // Move Modal state
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false)
  const [movingSets, setMovingSets] = useState(false)
  const [targetParentId, setTargetParentId] = useState<number | string | null>("null")

  // Reorder Modal state
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false)

  // System Set Settings Modal state (others_learning_ids, featured_ids)
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)

  const currentParentId = searchParams.parent_id || "null"
  const isRoot = !currentParentId || currentParentId === "null" || currentParentId === ""

  // 1. Query Sets in current level
  const {
    data: setsResult,
    isFetching: isFetchingSets,
    refetch: refetchSets,
  } = useQuery({
    queryKey: ["system_set_list", searchParams],
    queryFn: () => {
      const apiParams: SetApiParams = {
        page: searchParams.page,
        limit: searchParams.per_page,
        parent_id: currentParentId,
        type: "system",
        search: searchParams.search || undefined,
        visibility: searchParams.visibility || undefined,
      }
      return setService.list(apiParams)
    },
  })

  // 2. Query Breadcrumbs (When inside a subfolder)
  const { data: breadcrumbsResult } = useQuery({
    queryKey: ["set_breadcrumbs", currentParentId],
    queryFn: () => {
      if (isRoot) return Promise.resolve({ data: [] })
      return setService.getBreadcrumbs(Number(currentParentId))
    },
    enabled: !isRoot,
  })

  const breadcrumbs = breadcrumbsResult?.data || []
  const currentFolderName =
    breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].title : "Gốc (Root)"

  // 3. Query all folders for Move Modal
  const { data: foldersData } = useQuery({
    queryKey: ["folder_list_system"],
    queryFn: () => {
      return setService.list({
        page: 1,
        limit: 1000,
        node_type: "folder",
        type: "system",
      })
    },
    enabled: isMoveModalOpen,
  })

  const folders = foldersData?.data || []
  const folderOptions = [
    { value: "null", label: "🏠 Thư mục Gốc (Root)" },
    ...folders.map((f) => ({ value: f.id, label: `📁 ${f.title}` })),
  ].filter((f) => f.value === "null" || !selectedRowKeys.includes(Number(f.value)))

  const handleSelectionChange = (keys: React.Key[]) => {
    setSelectedRowKeys(keys)
  }

  const handleNavigateFolder = (folderId: number | string | null) => {
    setSelectedRowKeys([])
    setSearchParams(
      {
        ...searchParams,
        parent_id: folderId || "null",
        page: 1,
      },
      { replace: false }
    )
  }

  const handleCreateFolderSubmit = async () => {
    try {
      const values = await folderForm.validateFields()
      setCreatingFolder(true)
      const payload = {
        title: values.title,
        full_title: values.full_title || undefined,
        tags: values.tags || [],
        description: values.description,
        node_type: "folder" as const,
        parent_id: !isRoot ? Number(currentParentId) : undefined,
        file: createFolderImageFile,
      }
      const res = await setService.create(payload)
      if (res.code === 200) {
        message.success("Tạo thư mục thành công")
        folderForm.resetFields()
        setCreateFolderImageFile(null)
        setCreateFolderImagePreview(null)
        setIsFolderModalOpen(false)
        refetchSets()
      } else {
        message.error(res.message || "Tạo thư mục thất bại")
      }
    } catch (err: any) {
      if (err.message) {
        message.error(err.message)
      }
    } finally {
      setCreatingFolder(false)
    }
  }

  const handleCreateSetSubmit = async () => {
    try {
      const values = await setForm.validateFields()
      setCreatingSet(true)
      const payload = {
        title: values.title,
        full_title: values.full_title,
        tags: values.tags || [],
        description: values.description,
        node_type: "set" as const,
        parent_id: !isRoot ? Number(currentParentId) : undefined,
        vocabularies: [],
        file: createSetImageFile,
      }
      const res = await setService.create(payload)
      if (res.code === 200 && res.data?.id) {
        message.success("Tạo bộ từ vựng thành công")
        setForm.resetFields()
        setCreateSetImageFile(null)
        setCreateSetImagePreview(null)
        setIsSetModalOpen(false)
        navigate(`/notebook/system-set/edit/${res.data.id}`)
      } else {
        message.error(res.message || "Tạo bộ từ vựng thất bại")
      }
    } catch (err: any) {
      if (err.message) {
        message.error(err.message)
      }
    } finally {
      setCreatingSet(false)
    }
  }

  const handleEdit = (record: any) => {
    setEditingSet(record)
    const currentTags = Array.isArray(record.tags) ? record.tags : []
    editForm.setFieldsValue({
      title: record.title,
      full_title: record.full_title,
      slug: record.slug,
      tags: currentTags,
      description: record.description,
    })
    setEditSetImagePreview(record.image_url || record.image || null)
    setEditSetImageFile(null)
    setEditSetImageDeleted(false)
    setIsEditModalOpen(true)
  }

  const handleEditSubmit = async () => {
    try {
      const values = await editForm.validateFields()
      setUpdatingSet(true)

      const payload: any = {
        title: values.title,
        full_title: values.full_title,
        slug: values.slug,
        tags: values.tags || [],
        description: values.description,
      }

      if (editSetImageFile) {
        payload.file = editSetImageFile
      } else if (editSetImageDeleted) {
        payload.image = null
      }

      const res = await setService.update(editingSet.id, payload)
      if (res.code === 200) {
        message.success("Cập nhật thành công")
        setIsEditModalOpen(false)
        refetchSets()
      } else {
        message.error(res.message || "Cập nhật thất bại")
      }
    } catch (err: any) {
      if (err.message) {
        message.error(err.message)
      }
    } finally {
      setUpdatingSet(false)
    }
  }

  const handleDelete = async (record: any) => {
    try {
      const res = await setService.delete(record.id)
      if (res.code === 200) {
        message.success("Xoá thành công")
        refetchSets()
      } else {
        message.error(res.message || "Xoá thất bại")
      }
    } catch (err: any) {
      message.error(err.message || "Lỗi khi xoá")
    }
  }

  const handleMoveSubmit = async () => {
    if (selectedRowKeys.length === 0) return
    try {
      setMovingSets(true)
      const res = await setService.moveSets({
        ids: selectedRowKeys as number[],
        target_parent_id: targetParentId === "null" ? null : (targetParentId as number),
      })
      if (res.code === 200) {
        message.success(`Đã di chuyển thành công ${selectedRowKeys.length} mục!`)
        setIsMoveModalOpen(false)
        setSelectedRowKeys([])
        refetchSets()
      } else {
        message.error(res.message || "Di chuyển thất bại")
      }
    } catch (err: any) {
      message.error(err.message || "Lỗi khi di chuyển")
    } finally {
      setMovingSets(false)
    }
  }

  const handleUploadExcel = (file: File) => {
    setIsUploadingExcel(true)
    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const data = e.target?.result
        const workbook = XLSX.read(data, { type: "array" })

        let successCount = 0
        let totalVocabs = 0

        for (const sheetName of workbook.SheetNames) {
          const worksheet = workbook.Sheets[sheetName]
          const rows: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

          if (rows.length < 2) continue

          const headers = rows[0].map((h: any) =>
            String(h || "")
              .trim()
              .toLowerCase()
          )

          const vocabs = []
          for (let i = 1; i < rows.length; i++) {
            const cols = rows[i]
            if (!cols || !cols[0]) continue

            const item: any = { text: String(cols[0]).trim() }
            headers.forEach((h: string, idx: number) => {
              const val = cols[idx] !== undefined ? String(cols[idx]).trim() : ""
              h = String(h).trim().toLowerCase()
              if (h.includes("từ vựng") || h === "word") {
                item.text = val
              } else if (h.includes("từ loại") || h === "part of speech" || h === "pos") {
                item.part_of_speech = val
              } else if (h.includes("phiên âm") || h === "ipa") {
                item.ipa = val
              } else if (
                h.includes("nghĩa tiếng anh") ||
                h.includes("định nghĩa") ||
                h === "definition"
              ) {
                item.definition = val
              } else if (
                h.includes("nghĩa tiếng việt") ||
                h === "nghĩa" ||
                h === "meaning"
              ) {
                item.meaning = val
              } else if (h.includes("dịch ví dụ") || h.includes("nghĩa ví dụ")) {
                item.example_meaning = val
              } else if (h.includes("ví dụ") || h === "example") {
                item.example = val
              }
            })
            vocabs.push(item)
          }

          if (vocabs.length > 0) {
            const res = await setService.create({
              title: sheetName,
              node_type: "set",
              parent_id: !isRoot ? Number(currentParentId) : undefined,
            })

            if (res.code === 200 && res.data?.id) {
              const vocabRes = await vocabularyService.bulkAdd({
                set_id: res.data.id,
                vocabularies: vocabs,
              })
              if (vocabRes.code === 200) {
                successCount++
                totalVocabs += vocabs.length
              }
            }
          }
        }

        if (successCount > 0) {
          message.success(`Đã thêm thành công ${successCount} set với tổng ${totalVocabs} từ vựng!`)
          refetchSets()
        } else {
          message.warning("Không tìm thấy dữ liệu hợp lệ trong file Excel.")
        }
      } catch (err: any) {
        message.error(err.message || "Lỗi khi xử lý file Excel")
      } finally {
        setIsUploadingExcel(false)
      }
    }
    reader.readAsArrayBuffer(file)
    return false
  }

  const handleViewOnline = (_record: any) => {
    message.info("Tính năng xem online đang được phát triển")
  }

  // Breadcrumb items configuration starting from Root
  const breadcrumbItems = [
    {
      title: (
        <div
          className={`flex cursor-pointer items-center gap-1.5 font-medium ${isRoot ? "text-primary" : "text-gray-600 hover:text-primary"}`}
          onClick={() => handleNavigateFolder("null")}
        >
          <HomeOutlined /> Root
        </div>
      ),
    },
  ]

  breadcrumbs.forEach((b, index) => {
    const isLast = index === breadcrumbs.length - 1
    breadcrumbItems.push({
      title: (
        <div
          className={`flex cursor-pointer items-center gap-1 font-medium ${isLast ? "text-primary font-bold" : "text-gray-600 hover:text-primary"}`}
          onClick={() => handleNavigateFolder(b.id)}
        >
          <FolderOutlined /> {b.title}
        </div>
      ),
    })
  })

  return (
    <div className="flex flex-col gap-3">
      <style>{`
        .text-primary {
          color: var(--ant-color-primary, #1677ff) !important;
        }
        .border-primary {
          border-color: var(--ant-color-primary, #1677ff) !important;
        }
      `}</style>

      {/* Breadcrumbs Navigation */}
      <div className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 shadow-2xs">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <Card
        className="flex min-h-0 grow flex-col shadow-sm"
        classNames={{ body: "flex grow flex-col" }}
        title={
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center">
              <h2 className="text-base font-bold">
                {isRoot ? "Bộ từ vựng hệ thống" : `Thư mục "${currentFolderName}"`}
              </h2>
              {selectedRowKeys.length > 0 && (
                <>
                  <Divider orientation="vertical" />
                  <Text type="secondary" className="text-sm!">{`${selectedRowKeys.length} mục đã được chọn`}</Text>
                </>
              )}
            </div>

            {/* Actions Toolbar */}
            <div className="flex items-center gap-2">
              {selectedRowKeys.length > 0 && (
                <Button
                  type="primary"
                  size="small"
                  style={{ backgroundColor: "#fa8c16" }}
                  onClick={() => setIsMoveModalOpen(true)}
                >
                  Di chuyển ({selectedRowKeys.length})
                </Button>
              )}
              <Button
                type="default"
                size="small"
                icon={<SettingOutlined />}
                onClick={() => setIsSettingsModalOpen(true)}
              >
                Cấu hình
              </Button>
              <Button
                type="primary"
                size="small"
                icon={<FolderAddOutlined />}
                onClick={() => setIsFolderModalOpen(true)}
              >
                Tạo thư mục
              </Button>
              <Button
                type="primary"
                size="small"
                className="bg-green-600 hover:bg-green-500"
                icon={<FileAddOutlined />}
                onClick={() => setIsSetModalOpen(true)}
              >
                Tạo set
              </Button>
              <Button
                type="default"
                size="small"
                icon={<OrderedListOutlined />}
                onClick={() => setIsReorderModalOpen(true)}
              >
                Sắp xếp
              </Button>
              <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={handleUploadExcel}>
                <Button type="dashed" size="small" icon={<UploadOutlined />} loading={isUploadingExcel}>
                  Thêm nhanh (Excel)
                </Button>
              </Upload>
              <Tooltip title="Tải lại dữ liệu">
                <Button
                  type="default"
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => refetchSets()}
                  loading={isFetchingSets}
                />
              </Tooltip>
            </div>
          </div>
        }
      >
        <SystemSetTable
          data={setsResult?.data || []}
          loading={isFetchingSets}
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={handleSelectionChange}
          onRowClick={(record) => {
            if (record.node_type === "folder") {
              handleNavigateFolder(record.id)
            } else {
              navigate(`/notebook/system-set/edit/${record.id}`)
            }
          }}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onViewOnline={handleViewOnline}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total: setsResult?.paginate?.total || total,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page: page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>

      {/* Modal Creating Folder */}
      <Modal
        title="Tạo Thư mục mới"
        open={isFolderModalOpen}
        onOk={handleCreateFolderSubmit}
        confirmLoading={creatingFolder}
        onCancel={() => {
          folderForm.resetFields()
          setCreateFolderImageFile(null)
          setCreateFolderImagePreview(null)
          setIsFolderModalOpen(false)
        }}
        okText="Tạo mới"
        cancelText="Hủy"
      >
        <Form form={folderForm} layout="vertical" className="mt-4">
          <Form.Item
            name="title"
            label="Tên thư mục"
            rules={[{ required: true, message: "Vui lòng nhập tên thư mục" }]}
          >
            <Input placeholder="Ví dụ: Unit 1, Tiếng Anh Giao Tiếp..." />
          </Form.Item>
          <Form.Item name="full_title" label="Tên đầy đủ (Full Title)">
            <Input placeholder="Ví dụ: Tiếng Anh Lớp 12 - Học kỳ 1..." />
          </Form.Item>
          <Form.Item name="tags" label="Tags (Nhãn phân loại)">
            <Select
              mode="multiple"
              placeholder="Chọn các nhãn phân loại"
              options={SET_TAG_OPTIONS}
              allowClear
            />
          </Form.Item>
          <Form.Item name="description" label="Mô tả thư mục">
            <Input.TextArea rows={3} placeholder="Nhập mô tả ngắn..." />
          </Form.Item>
          <Form.Item label="Ảnh đại diện (Cover Image)">
            <div className="flex items-center gap-3">
              {createFolderImagePreview && (
                <div className="relative group">
                  <img
                    src={createFolderImagePreview}
                    alt="Preview"
                    className="h-16 w-16 rounded-lg border border-gray-200 object-cover shadow-xs"
                  />
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md"
                    onClick={() => {
                      setCreateFolderImageFile(null)
                      setCreateFolderImagePreview(null)
                    }}
                  />
                </div>
              )}
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={(file) => {
                  setCreateFolderImageFile(file)
                  setCreateFolderImagePreview(URL.createObjectURL(file))
                  return false
                }}
              >
                <Button icon={<UploadOutlined />} size="small">
                  {createFolderImagePreview ? "Đổi ảnh khác" : "Chọn ảnh từ máy"}
                </Button>
              </Upload>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Creating Set */}
      <Modal
        title="Tạo Bộ từ vựng (Set) mới"
        open={isSetModalOpen}
        onOk={handleCreateSetSubmit}
        confirmLoading={creatingSet}
        onCancel={() => {
          setForm.resetFields()
          setCreateSetImageFile(null)
          setCreateSetImagePreview(null)
          setIsSetModalOpen(false)
        }}
        okText="Lưu và thêm từ vựng"
        cancelText="Hủy"
      >
        <Form form={setForm} layout="vertical" className="mt-4">
          <Form.Item
            name="title"
            label="Tên bộ từ"
            rules={[{ required: true, message: "Vui lòng nhập tên bộ từ" }]}
          >
            <Input placeholder="Ví dụ: Speaking Writing, Unit 1..." />
          </Form.Item>
          <Form.Item name="full_title" label="Tên đầy đủ (Full Title)">
            <Input placeholder="Ví dụ: Lớp 12 - Unit 6: Speaking Writing..." />
          </Form.Item>
          <Form.Item name="tags" label="Tags (Nhãn phân loại)">
            <Select
              mode="multiple"
              placeholder="Chọn các nhãn phân loại"
              options={SET_TAG_OPTIONS}
              allowClear
            />
          </Form.Item>
          <Form.Item name="description" label="Mô tả">
            <Input.TextArea rows={3} placeholder="Nhập mô tả ngắn..." />
          </Form.Item>
          <Form.Item label="Ảnh đại diện (Cover Image)">
            <div className="flex items-center gap-3">
              {createSetImagePreview && (
                <div className="relative group">
                  <img
                    src={createSetImagePreview}
                    alt="Preview"
                    className="h-16 w-16 rounded-lg border border-gray-200 object-cover shadow-xs"
                  />
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md"
                    onClick={() => {
                      setCreateSetImageFile(null)
                      setCreateSetImagePreview(null)
                    }}
                  />
                </div>
              )}
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={(file) => {
                  setCreateSetImageFile(file)
                  setCreateSetImagePreview(URL.createObjectURL(file))
                  return false
                }}
              >
                <Button icon={<UploadOutlined />} size="small">
                  {createSetImagePreview ? "Đổi ảnh khác" : "Chọn ảnh từ máy"}
                </Button>
              </Upload>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Edit Set/Folder */}
      <Modal
        title={`Sửa ${editingSet?.node_type === "folder" ? "Thư mục" : "Bộ từ"}`}
        open={isEditModalOpen}
        onOk={handleEditSubmit}
        confirmLoading={updatingSet}
        onCancel={() => {
          editForm.resetFields()
          setEditSetImagePreview(null)
          setEditSetImageFile(null)
          setEditSetImageDeleted(false)
          setIsEditModalOpen(false)
        }}
        okText="Lưu thay đổi"
        cancelText="Hủy"
      >
        <Form form={editForm} layout="vertical" className="mt-4">
          <Form.Item name="title" label="Tên" rules={[{ required: true, message: "Vui lòng nhập tên" }]}>
            <Input placeholder="Ví dụ: Unit 1..." />
          </Form.Item>
          <Form.Item name="full_title" label="Tên đầy đủ (Full Title)">
            <Input placeholder="Ví dụ: Tiếng Anh Lớp 12 - Unit 6: Speaking Writing..." />
          </Form.Item>
          <Form.Item
            name="slug"
            label="Slug (Đường dẫn tĩnh)"
            rules={[
              {
                pattern: /^[a-z0-9-]+$/,
                message: "Slug chỉ bao gồm chữ thường không dấu, số và dấu gạch ngang",
              },
            ]}
            extra="Tùy chỉnh đường dẫn URL (SEO / Friendly URL)"
          >
            <Input placeholder="vi-du-duong-dan-slug" />
          </Form.Item>
          <Form.Item name="tags" label="Tags (Nhãn phân loại)">
            <Select
              mode="multiple"
              placeholder="Chọn các nhãn phân loại"
              options={SET_TAG_OPTIONS}
              allowClear
            />
          </Form.Item>
          <Form.Item name="description" label="Mô tả">
            <Input.TextArea rows={3} placeholder="Nhập mô tả ngắn..." />
          </Form.Item>
          <Form.Item label="Ảnh đại diện (Cover Image)">
            <div className="flex items-center gap-3">
              {editSetImagePreview && (
                <div className="relative group">
                  <img
                    src={editSetImagePreview}
                    alt="Preview"
                    className="h-16 w-16 rounded-lg border border-gray-200 object-cover shadow-xs"
                  />
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md"
                    onClick={() => {
                      setEditSetImagePreview(null)
                      setEditSetImageFile(null)
                      setEditSetImageDeleted(true)
                    }}
                  />
                </div>
              )}
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={(file) => {
                  setEditSetImageFile(file)
                  setEditSetImagePreview(URL.createObjectURL(file))
                  setEditSetImageDeleted(false)
                  return false
                }}
              >
                <Button icon={<UploadOutlined />} size="small">
                  {editSetImagePreview ? "Đổi ảnh khác" : "Chọn ảnh từ máy"}
                </Button>
              </Upload>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Move Sets */}
      <Modal
        title="Di chuyển thư mục / bộ từ"
        open={isMoveModalOpen}
        onOk={handleMoveSubmit}
        confirmLoading={movingSets}
        onCancel={() => setIsMoveModalOpen(false)}
        okText="Xác nhận di chuyển"
        cancelText="Hủy"
      >
        <div className="mt-4 flex flex-col gap-2">
          <Text>Chọn thư mục đích để di chuyển {selectedRowKeys.length} mục đã chọn:</Text>
          <Select
            className="w-full"
            value={targetParentId}
            onChange={(val) => setTargetParentId(val)}
            options={folderOptions}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
      </Modal>

      {/* Modal Reorder Sets */}
      <SystemSetReorderModal
        open={isReorderModalOpen}
        onClose={() => setIsReorderModalOpen(false)}
        onSuccess={() => refetchSets()}
        parentId={currentParentId}
        currentFolderName={currentFolderName}
      />

      {/* Modal System Set Settings (others_learning_ids, featured_ids) */}
      <SystemSetSettingsModal
        open={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
      />
    </div>
  )
}

export default SystemSetPage
